import React, { useEffect, useState } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { config } from '../../../../config/envs';
import Disable2FAModal from './Disable2FAModal';

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
}

function StatusBadge({
  hasTwoFactorEnabled,
  isTwoFactorDeactivated,
  reason,
  userId,
}) {
  let badge;
  let tooltipText = null;

  if (!hasTwoFactorEnabled) {
    badge = (
      <span
        className="badge badge-light-secondary font-weight-bold text-secondary"
        style={{ backgroundColor: 'rgba(108, 117, 125, 0.12)' }}
      >
        Não configurado
      </span>
    );
    tooltipText = '2FA nunca foi configurado';
  } else if (isTwoFactorDeactivated) {
    badge = (
      <span
        className="badge badge-light-danger font-weight-bold text-danger"
        style={{ backgroundColor: 'rgba(220, 53, 69, 0.12)' }}
      >
        Temporariamente desativado
      </span>
    );
    if (reason) {
      tooltipText = `Motivo: ${reason}`;
    }
  } else {
    badge = (
      <span
        className="badge badge-light-success font-weight-bold text-success"
        style={{ backgroundColor: 'rgba(40, 167, 69, 0.12)' }}
      >
        Habilitado
      </span>
    );
  }

  if (tooltipText) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={`tooltip-status-${userId}`}>{tooltipText}</Tooltip>}
      >
        {badge}
      </OverlayTrigger>
    );
  }

  return badge;
}

export default function AccountSecurity({ clientId }) {
  const [loading, setLoading] = useState(false);
  const [usersSecurity, setUsersSecurity] = useState([]);
  const [rowLoading, setRowLoading] = useState(null);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadUsersSecurity = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${config.app.adminBaseUrl}/account/${clientId}/security-info`,
      );
      setUsersSecurity(data);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        'Não foi possível carregar os status de 2FA.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsersSecurity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const toggleTwoFactor = async (user, reason) => {
    if (!user || !user.hasTwoFactorEnabled) return;
    setRowLoading(user.id);
    try {
      const trimmedReason = reason ? reason.trim() : '';
      const path = user.isTwoFactorDeactivated
        ? `/account/${user.id}/enable-two-factor`
        : `/account/${user.id}/disable-two-factor`;

      const { data } = await axios.post(`${config.app.adminBaseUrl}${path}`, {
        reason: trimmedReason,
      });

      if (data.message) {
        toast.success(data.message);
      }

      await loadUsersSecurity();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        'Não foi possível alternar o status do 2FA.';
      toast.error(msg);
    } finally {
      setRowLoading(null);
    }
  };

  const handleActionClick = (user) => {
    if (user.isTwoFactorDeactivated) {
      toggleTwoFactor(user);
    } else {
      setSelectedUser(user);
      setShowDisableModal(true);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-6">
            <div>
              <h4 className="mb-1">Segurança da Conta</h4>
              <span className="text-muted">
                Visualização de 2FA do titular e subusuários.
              </span>
            </div>
          </div>

          {loading ? (
            <div className="d-flex align-items-center">
              <span className="spinner-border spinner-border-sm mr-3"></span>
              <span>Carregando status de segurança...</span>
            </div>
          ) : (
            <div className="card card-custom">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  Usuários e status de autenticação de dois fatores
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-head-custom table-vertical-center mb-0">
                    <thead>
                      <tr className="text-uppercase text-muted">
                        <th className="pl-7">Usuário</th>
                        <th>Email</th>
                        <th>Status 2FA</th>
                        <th>Expira em</th>
                        <th className="text-right pr-7">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersSecurity.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-6 text-muted"
                          >
                            Nenhum usuário encontrado.
                          </td>
                        </tr>
                      )}
                      {usersSecurity.map((user) => (
                        <tr key={user.id}>
                          <td className="pl-7">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                {user.isPrimary && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip
                                        id={`tooltip-primary-${user.id}`}
                                      >
                                        Esse usuário é o titular
                                      </Tooltip>
                                    }
                                  >
                                    <span className="mr-2 text-warning">★</span>
                                  </OverlayTrigger>
                                )}
                                <span className="text-dark-75 font-weight-bolder">
                                  {user.name}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <StatusBadge
                              hasTwoFactorEnabled={user.hasTwoFactorEnabled}
                              isTwoFactorDeactivated={
                                user.isTwoFactorDeactivated
                              }
                              reason={user.reason}
                              userId={user.id}
                            />
                          </td>
                          <td className="text-muted">
                            {user.hasTwoFactorEnabled &&
                            user.isTwoFactorDeactivated &&
                            user.expires_at
                              ? formatDateTime(user.expires_at)
                              : '-'}
                          </td>
                          <td className="text-right pr-7">
                            {user.hasTwoFactorEnabled ? (
                              <Dropdown alignRight>
                                <Dropdown.Toggle
                                  variant="link"
                                  className="btn btn-clean btn-icon btn-sm"
                                  disabled={rowLoading === user.id}
                                >
                                  {rowLoading === user.id ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                  ) : (
                                    <i className="ki ki-bold-more-hor"></i>
                                  )}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="dropdown-menu-sm">
                                  <Dropdown.Item
                                    onClick={() => handleActionClick(user)}
                                    disabled={rowLoading === user.id}
                                    style={{ whiteSpace: 'normal' }}
                                  >
                                    {user.isTwoFactorDeactivated
                                      ? 'Reativar 2FA'
                                      : 'Desativar 2FA temporariamente'}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            ) : (
                              <span className="text-muted font-size-sm">
                                N/A
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Disable2FAModal
        show={showDisableModal}
        onHide={() => setShowDisableModal(false)}
        onConfirm={async (reason) => {
          if (selectedUser) {
            await toggleTwoFactor(selectedUser, reason);
          }
          setShowDisableModal(false);
        }}
        user={selectedUser}
        loading={rowLoading === selectedUser?.id}
      />
    </>
  );
}
